import { CSSProperties, ReactNode } from 'react';
import { uiFieldTypeIconsMap } from '../../redux/modules/schema/helpers';
import { OriginalDatapointSchema, UiFieldType } from '../../types/schema';

type Props = Pick<
  OriginalDatapointSchema,
  'width' | 'label' | 'stretch' | 'type'
> & {
  uiFieldType: UiFieldType;
  widthOverride?: number;
};

type ComponentProps = {
  children: ReactNode;
  className?: string;
} & Props;

const PADDING = 41;
const CHAR_WIDTH_IN_NEW_FOOTER = 9;
const ICON_WIDTH = 28;

type CellWidthDefaults = {
  enum: () => number;
  number: () => number;
  string: () => number;
  date: () => number;
  button: (label: string) => number;
  [key: string]: (label: string) => number;
};

const defaults: CellWidthDefaults = {
  enum: () => 20,
  number: () => 8,
  string: () => 20,
  date: () => 10,
  button: (label: string) => label.length,
};

export const calculateWidth = ({
  width,
  type,
  label,
  uiFieldType,
}: Omit<Props, 'stretch' | 'widthOverride'>) => {
  const iconWidth =
    uiFieldType && uiFieldTypeIconsMap[uiFieldType] ? ICON_WIDTH : 0;
  const sizeAddition = iconWidth + PADDING;

  return width
    ? width * CHAR_WIDTH_IN_NEW_FOOTER + sizeAddition
    : (defaults[type] ?? defaults.string)(label) * CHAR_WIDTH_IN_NEW_FOOTER +
        sizeAddition;
};

const getCustomStyle = ({
  width,
  type,
  stretch,
  label,
  uiFieldType,
  widthOverride,
}: Props): CSSProperties => {
  const calculatedWidth =
    widthOverride ?? calculateWidth({ width, type, label, uiFieldType });

  return {
    minWidth: calculatedWidth,
    maxWidth: stretch ? undefined : calculatedWidth,
    textAlign: type === 'number' ? 'right' : 'left',
  };
};

const FooterCell = ({
  className,
  children,
  width,
  stretch,
  label,
  type,
  uiFieldType,
  widthOverride,
}: ComponentProps) => (
  <div
    style={{
      ...getCustomStyle({
        width,
        stretch,
        label,
        type,
        uiFieldType,
        widthOverride,
      }),
      flex: '1 1 0',
    }}
    className={className}
  >
    {children}
  </div>
);

export default FooterCell;
